import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Card, Checkbox, Col, InputNumber, Layout, Pagination, Popover, Row, Select, Space, Table, Tag, Typography } from "antd";
import { formatNumber } from "../../../util/NumberFormatter";
import MobileStatBox from "../../common/MobileStatBox";
import useIsMobile from "../../../util/useIsMobile";

const tierOrder = ['Normal', 'Iron', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond', 'Meteorite', 'Mithril'];
const tierDisplayMap = {
  'Mithril': '미스릴+',
  'Meteorite': '메테오라이트+',
  'Diamond': '다이아몬드+',
  'Platinum': '플래티넘+',
  'Gold': '골드+',
  'Silver': '실버+',
  'Bronze': '브론즈+',
  'Iron': '아이언+',
  'Normal': '전체(일반포함)'
};
const tierDropdownOptions = tierOrder.slice().reverse().map(tier => {
  const imagePath = `/img/tier/${tier}.png`;
  return {
    value: tier,
    label: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={imagePath}
          alt={tier}
          style={{ height: '20px', width: 'auto', marginRight: '8px' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span>{tierDisplayMap[tier]}</span>
      </div>
    ),
  };
});

const archeTypeColorMap = {
  '탱커': '#4a90d9',
  '전사': '#d94a4a',
  '암살자': '#8b5cf6',
  '스킬 딜러': '#e67e22',
  '원거리 딜러': '#27ae60',
  '지원가': '#f1c40f',
};

const getOutlierStyle = (value, average, lowerIsBetter = false) => {
  if (!average || isNaN(value)) return {};
  const lower = average * 0.85;
  const upper = average * 1.15;
  if (lowerIsBetter) {
    if (value < lower) return { color: 'red', fontWeight: 'bold' };
    if (value > upper) return { color: '#07ae04' };
  } else {
    if (value > upper) return { color: 'red', fontWeight: 'bold' };
    if (value < lower) return { color: '#07ae04' };
  }
  return {};
};

function CharacterWithWeapon({ name, weapon, archeType }) {
  const tagColor = archeTypeColorMap[archeType] || '#999';
  const isArcheTypeSlot = !weapon;

  if (isArcheTypeSlot) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <Tag color={tagColor} style={{ fontSize: 13, lineHeight: '28px', padding: '0 10px' }}>
          {archeType}
        </Tag>
      </div>
    );
  }

  const characterImgSrc = `/img/character/${name}.png`;
  const weaponImgSrc = `/img/weapon_skill/${weapon}.png`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
      <div style={{ position: 'relative', width: 32, height: 32, marginRight: 6, flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', backgroundColor: '#d6d6d6',
          overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src={characterImgSrc} alt={name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
        <img src={weaponImgSrc} alt={weapon}
          style={{
            position: 'absolute', bottom: 0, right: -6, width: 14, height: 14,
            borderRadius: '50%', backgroundColor: '#323232', padding: 1, border: '1px solid #4A4A4A',
          }}
          onError={(e) => { e.target.style.display = 'none'; }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{name} [{weapon}]</span>
        <Tag color={tagColor} style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', width: 'fit-content' }}>
          {archeType}
        </Tag>
      </div>
    </div>
  );
}

function TeamCompMobileCard({ stat, index, averages, totalGameCount }) {
  const hasGames = stat.gameCount > 0;
  const pickRate = totalGameCount > 0 ? (stat.gameCount / totalGameCount) * 100 : 0;
  const winRate = hasGames ? (stat.victory / stat.gameCount) * 100 : 0;
  const top3Rate = hasGames ? (stat.top3 / stat.gameCount) * 100 : 0;
  const avgRank = hasGames ? (stat.gameRank / stat.gameCount) : null;
  const rpGain = hasGames ? (stat.mmrGain / stat.gameCount) : null;
  const tkPerGame = hasGames ? (stat.teamKill / stat.gameCount) : 0;
  const tierImgSrc = `/img/character_tier/${stat.characterTier}.svg`;

  return (
    <Card size="small" style={{ marginBottom: 8 }} bodyStyle={{ padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ width: 22, textAlign: 'center', fontWeight: 'bold', flexShrink: 0, paddingTop: 4 }}>
          {index + 1}
        </div>
        <div style={{ flex: 1 }}>
          <CharacterWithWeapon name={stat.characterName1} weapon={stat.bestWeapon1} archeType={stat.archeType1} />
          <CharacterWithWeapon name={stat.characterName2} weapon={stat.bestWeapon2} archeType={stat.archeType2} />
          <CharacterWithWeapon name={stat.characterName3} weapon={stat.bestWeapon3} archeType={stat.archeType3} />
        </div>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#ffffff',
          overflow: 'hidden', flexShrink: 0, boxShadow: '0 0 0 1px #f0f0f0',
        }}>
          <img
            src={tierImgSrc}
            alt={stat.characterTier}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>
      <Row gutter={[4, 6]}>
        <Col span={8}><MobileStatBox label="픽률" value={`${pickRate.toFixed(2)}% (${stat.gameCount})`} /></Col>
        <Col span={8}>
          <MobileStatBox
            label="승률"
            value={hasGames ? `${winRate.toFixed(2)}%` : '-'}
            style={getOutlierStyle(winRate / 100, averages.avgWinRate, false)}
          />
        </Col>
        <Col span={8}><MobileStatBox label="Top3" value={hasGames ? `${top3Rate.toFixed(2)}%` : '-'} /></Col>
        <Col span={8}>
          <MobileStatBox
            label="RP"
            value={rpGain === null ? '-' : rpGain.toFixed(2)}
            style={rpGain === null ? undefined : getOutlierStyle(rpGain, averages.avgMmrGain, false)}
          />
        </Col>
        <Col span={8}>
          <MobileStatBox
            label="평균등수"
            value={avgRank === null ? '-' : avgRank.toFixed(2)}
            style={avgRank === null ? undefined : getOutlierStyle(avgRank, averages.avgRank, true)}
          />
        </Col>
        <Col span={8}><MobileStatBox label="TK" value={tkPerGame.toFixed(2)} /></Col>
      </Row>
    </Card>
  );
}

export default function TeamComp() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);
  const [selectedMinTier, setSelectedMinTier] = useState('Diamond');
  const [daysAgo, setDaysAgo] = useState(14);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [groupByArcheType, setGroupByArcheType] = useState(false);
  const [characterOptions, setCharacterOptions] = useState([]);
  const [mobilePage, setMobilePage] = useState(1);
  const isMobile = useIsMobile();
  const mobilePageSize = 100;

  // 캐릭터 목록 로드 (top-ranker-stats에서 캐릭터 이름 추출)
  useEffect(() => {
    axios.get("/api/external/bser/top-ranker-stats", {
      params: { days: 3, tier: 'Diamond', mainWeathers: '흐림,쾌청,비,모래바람', subWeathers: '무풍,안개,강풍,벼락' }
    })
      .then(r => r.data)
      .then(data => {
        const uniqueNames = [...new Set(data.map(d => d.characterName))].sort();
        setCharacterOptions(uniqueNames.map(name => ({
          value: name,
          label: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={`/img/character/${name}.png`} alt={name}
                style={{ width: 20, height: 20, borderRadius: '50%', marginRight: 8, backgroundColor: '#d6d6d6' }}
                onError={(e) => { e.target.style.display = 'none'; }} />
              <span>{name}</span>
            </div>
          ),
        })));
      });
  }, []);

  // 조합 통계 조회
  useEffect(() => {
    setLoading(true);
    var params = { days: daysAgo, tier: selectedMinTier, groupByArcheType };
    if (selectedCharacters.length > 0) {
      params.characterNames = selectedCharacters.join(',');
    }
    axios.get("/api/external/bser/team-comp-stats", { params })
      .then(r => r.data)
      .then(data => {
        const sorted = data
          .sort((a, b) => b.score - a.score);
        setStats(sorted);
        setMobilePage(1);
      })
      .finally(() => setLoading(false));
  }, [daysAgo, selectedMinTier, selectedCharacters, groupByArcheType]);

  const totalGameCount = useMemo(() => stats.reduce((sum, s) => sum + s.gameCount, 0), [stats]);

  const averages = useMemo(() => {
    if (!stats || stats.length === 0) return {};
    let sumRank = 0, sumWin = 0, sumTop3 = 0, sumMmr = 0, count = 0;
    stats.forEach(s => {
      if (s.gameCount > 0) {
        sumRank += s.gameRank / s.gameCount;
        sumWin += s.victory / s.gameCount;
        sumTop3 += s.top3 / s.gameCount;
        sumMmr += s.mmrGain / s.gameCount;
        count++;
      }
    });
    return {
      avgRank: count > 0 ? sumRank / count : null,
      avgWinRate: count > 0 ? sumWin / count : null,
      avgTop3Rate: count > 0 ? sumTop3 / count : null,
      avgMmrGain: count > 0 ? sumMmr / count : null,
    };
  }, [stats]);

  const handlePresetClick = (presetDays) => {
    setDaysAgo(presetDays);
    setPopoverVisible(false);
  };

  const popoverContent = (
    <Space direction="vertical" style={{ width: '250px' }}>
      <InputNumber min={14} max={31} value={daysAgo}
        onChange={(v) => setDaysAgo(v === null ? 14 : Math.max(14, v))} addonAfter="일 전" style={{ width: '100%' }} />
      <Space wrap>
        <Button onClick={() => handlePresetClick(14)}>2주 전</Button>
        <Button onClick={() => handlePresetClick(21)}>3주 전</Button>
        <Button onClick={() => handlePresetClick(31)}>한 달 전</Button>
      </Space>
    </Space>
  );

  const columns = [
    {
      title: '순위',
      align: 'center',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: '조합',
      width: 320,
      render: (stat) => (
        <div>
          <CharacterWithWeapon name={stat.characterName1} weapon={stat.bestWeapon1} archeType={stat.archeType1} />
          <CharacterWithWeapon name={stat.characterName2} weapon={stat.bestWeapon2} archeType={stat.archeType2} />
          <CharacterWithWeapon name={stat.characterName3} weapon={stat.bestWeapon3} archeType={stat.archeType3} />
        </div>
      ),
    },
    {
      title: '픽 수',
      align: 'center',
      dataIndex: 'gameCount',
      sorter: (a, b) => a.gameCount - b.gameCount,
      render: (gameCount) => (
        <span>
          <div>{totalGameCount > 0 ? ((gameCount / totalGameCount) * 100).toFixed(2) : 0}%</div>
          <div style={{ fontSize: '0.77em' }}>({formatNumber(gameCount)})</div>
        </span>
      ),
    },
    {
      title: 'Score',
      align: 'center',
      sorter: (a, b) => a.score - b.score,
      render: (stat) => {
        const src = `/img/character_tier/${stat.characterTier}.svg`;
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <img src={src} alt={stat.characterTier}
              style={{ width: 36, height: 36 }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
        );
      },
    },
    {
      title: 'RP 획득량',
      align: 'center',
      sorter: (a, b) => (a.mmrGain / a.gameCount) - (b.mmrGain / b.gameCount),
      render: (stat) => {
        const val = stat.mmrGain / stat.gameCount;
        return <span style={getOutlierStyle(val, averages.avgMmrGain)}>{val.toFixed(2)}</span>;
      },
    },
    {
      title: '평균 등수',
      align: 'center',
      sorter: (a, b) => (a.gameRank / a.gameCount) - (b.gameRank / b.gameCount),
      render: (stat) => {
        const val = stat.gameRank / stat.gameCount;
        return <span style={getOutlierStyle(val, averages.avgRank, true)}>{val.toFixed(2)}</span>;
      },
    },
    {
      title: '승률',
      align: 'center',
      sorter: (a, b) => (a.victory / a.gameCount) - (b.victory / b.gameCount),
      render: (stat) => {
        const rate = stat.victory / stat.gameCount;
        return (
          <span style={getOutlierStyle(rate, averages.avgWinRate)}>
            <div>{(rate * 100).toFixed(2)}%</div>
            <div style={{ fontSize: '0.77em' }}>({stat.victory})</div>
          </span>
        );
      },
    },
    {
      title: 'Top 3',
      align: 'center',
      sorter: (a, b) => (a.top3 / a.gameCount) - (b.top3 / b.gameCount),
      render: (stat) => {
        const rate = stat.top3 / stat.gameCount;
        return (
          <span style={getOutlierStyle(rate, averages.avgTop3Rate)}>
            <div>{(rate * 100).toFixed(2)}%</div>
            <div style={{ fontSize: '0.77em' }}>({stat.top3})</div>
          </span>
        );
      },
    },
    {
      title: 'TK',
      align: 'center',
      sorter: (a, b) => (a.teamKill / a.gameCount) - (b.teamKill / b.gameCount),
      render: (stat) => {
        if (stat.gameCount === 0) return '-';
        return <span>{(stat.teamKill / stat.gameCount).toFixed(2)}</span>;
      },
    },
  ];

  return (
    <Layout style={{ background: '#ffffff' }}>
      <Layout.Content style={{ padding: '20px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
            <Space direction="horizontal" align="center" wrap>
              <Popover
                content={popoverContent}
                title={<Typography.Text strong>기간 선택</Typography.Text>}
                trigger="click"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
              >
                <Button style={{ minWidth: 120 }}>{`${daysAgo}일 전`}</Button>
              </Popover>
              <Select
                value={selectedMinTier}
                style={{ width: 180 }}
                onChange={setSelectedMinTier}
                options={tierDropdownOptions}
                listHeight={288}
              />
              <Select
                mode="multiple"
                allowClear
                placeholder="캐릭터 선택 (최대 3개)"
                value={selectedCharacters}
                onChange={(values) => {
                  if (values.length <= 3) setSelectedCharacters(values);
                }}
                options={characterOptions}
                style={{ minWidth: 280 }}
                maxTagCount={3}
                showSearch
                filterOption={(input, option) =>
                  option.value.toLowerCase().includes(input.toLowerCase())
                }
              />
              <Checkbox
                checked={groupByArcheType}
                onChange={(e) => setGroupByArcheType(e.target.checked)}
              >
                역할군별 묶기
              </Checkbox>
            </Space>
            <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              조합 수: {formatNumber(stats.length)} / 총 픽 수: {formatNumber(totalGameCount)}
            </div>
          </div>
          {isMobile ? (
            <div>
              {stats.slice((mobilePage - 1) * mobilePageSize, mobilePage * mobilePageSize).map((stat, idx) => (
                <TeamCompMobileCard
                  key={`${stat.characterName1}-${stat.bestWeapon1}-${stat.characterName2}-${stat.bestWeapon2}-${stat.characterName3}-${stat.bestWeapon3}`}
                  stat={stat}
                  index={(mobilePage - 1) * mobilePageSize + idx}
                  averages={averages}
                  totalGameCount={totalGameCount}
                />
              ))}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <Pagination
                  current={mobilePage}
                  pageSize={mobilePageSize}
                  total={stats.length}
                  showSizeChanger={false}
                  onChange={setMobilePage}
                />
              </div>
            </div>
          ) : (
            <Table
              className='table-wrapper-with-border'
              pagination={{ pageSize: 100, showSizeChanger: false, showTotal: (total) => `총 ${total}개` }}
              dataSource={stats}
              columns={columns}
              scroll={{ x: 'max-content' }}
              loading={loading}
              rowKey={(record) => `${record.characterName1}-${record.bestWeapon1}-${record.characterName2}-${record.bestWeapon2}-${record.characterName3}-${record.bestWeapon3}`}
            />
          )}
        </Space>
      </Layout.Content>
    </Layout>
  );
}
